import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import HeroCarousel from "@/components/feature/HeroCarousel";
import TitleCard from "@/components/feature/TitleCard";
import SectionTitle from "@/components/base/SectionTitle";
import CategoryShortcuts from "@/pages/home/components/CategoryShortcuts";
import LatestNewsFeed from "@/pages/home/components/LatestNewsFeed";
import { titles } from "@/mocks/titles";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function Home() {
  usePageMeta(
    "奇优影院-优质电影电视剧在线资讯平台",
    "奇优影院提供丰富多样的电影和电视剧资讯，涵盖最新上映影片和热门剧集推荐，满足用户多样化影视需求。",
    "奇优影院,电影,电视剧,影视资讯,热门影片",
  );

  const hotTitles = [...titles].sort((a, b) => b.hot - a.hot).slice(0, 10);
  const moviePicks = titles.filter((item) => item.type === "movie").slice(0, 5);
  const tvPicks = titles.filter((item) => item.type === "tv").slice(0, 5);

  return (
    <SiteLayout>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <section className="pt-6 md:pt-9 pb-5 md:pb-7">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            <div>
              <p className="text-[11px] md:text-xs tracking-[0.22em] text-accent-600 font-label uppercase mb-2">
                Qiyou Cinema · 影视资讯与推荐
              </p>
              <h1 className="text-2xl md:text-3xl lg:text-[34px] font-extrabold text-foreground-950 font-heading leading-tight text-balance">
                奇优影院 · 优质电影电视剧在线资讯平台
              </h1>
              <p className="text-sm md:text-[15px] text-foreground-600 mt-2.5 max-w-2xl leading-relaxed">
                聚合最新上映影片、热播剧集与行业动态，按类型、地区、年份快速筛选，查看作品简介与分集目录，把想看的片子一次找齐。
              </p>
            </div>
            <div className="flex items-center gap-5 md:gap-7 shrink-0">
              {[
                { value: "16", label: "在库作品" },
                { value: "08", label: "今日资讯" },
                { value: "每日", label: "持续更新" },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <p className="text-xl md:text-2xl font-extrabold font-heading text-primary-700">{stat.value}</p>
                  <p className="text-[11px] md:text-xs text-foreground-500 mt-0.5">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-5">
          <div>
            <HeroCarousel />
            <CategoryShortcuts />
          </div>
          <LatestNewsFeed />
        </div>

        <section className="mt-10 md:mt-12">
          <SectionTitle
            eyebrow="TOP PICKS"
            title="本周热度最高的影视作品"
            description="按站内热度排序，覆盖正在热议的院线新片与热播剧集。"
            actionLabel="查看全部电影"
            actionTo="/category/movie"
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
            {hotTitles.map((item) => (
              <TitleCard key={item.id} item={item} />
            ))}
          </div>
        </section>

        <section className="mt-10 md:mt-12 rounded-lg bg-background-100 border border-background-200 p-4 md:p-6">
          <SectionTitle
            eyebrow="NOW SHOWING"
            title="院线新片精选"
            actionLabel="进入电影分类"
            actionTo="/category/movie"
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
            {moviePicks.map((item) => (
              <TitleCard key={item.id} item={item} compact />
            ))}
          </div>
        </section>

        <section className="mt-6 rounded-lg bg-background-50 border border-background-200 p-4 md:p-6">
          <SectionTitle
            eyebrow="ON AIR"
            title="正在热播的剧集"
            actionLabel="进入电视剧分类"
            actionTo="/category/tv"
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
            {tvPicks.map((item) => (
              <TitleCard key={item.id} item={item} compact />
            ))}
          </div>
        </section>

        <section className="mt-10 md:mt-12 rounded-lg bg-primary-950 px-5 py-8 md:px-10 md:py-11">
          <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-7 items-center">
            <div>
              <p className="text-[11px] tracking-[0.22em] text-accent-400 font-label uppercase mb-2">
                About Qiyou Cinema
              </p>
              <h2 className="text-xl md:text-3xl font-extrabold text-background-50 font-heading leading-snug">
                不做播放器，只做你看片前的第一站
              </h2>
              <p className="text-sm text-background-300/90 mt-3 leading-relaxed max-w-lg">
                奇优影院专注于影视内容的整理与推荐：清晰的分类筛选、完整的作品信息、可查阅的分集目录，以及每天更新的行业资讯。所有片名与演职人员均为虚构示例，本站不提供在线播放。
              </p>
              <Link
                to="/about"
                className="mt-5 inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                了解我们的平台
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { icon: "ri-play-list-2-line", title: "分类速查", desc: "电影 / 剧集 / 资讯三类入口" },
                { icon: "ri-filter-3-line", title: "多维筛选", desc: "类型、地区、年份组合筛选" },
                { icon: "ri-heart-3-line", title: "本地收藏", desc: "一键收藏，随时找回（演示）" },
              ].map((feature) => (
                <div key={feature.title} className="rounded-lg bg-primary-900/70 border border-primary-800 p-3.5">
                  <span className="w-8 h-8 rounded-md bg-primary-800 text-secondary-400 flex items-center justify-center mb-2">
                    <i className={`${feature.icon} text-base w-4 h-4 flex items-center justify-center`} />
                  </span>
                  <p className="text-sm font-semibold text-background-50">{feature.title}</p>
                  <p className="text-xs text-background-400 mt-1 leading-relaxed">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}